// A session's turns and its prompt cache, as today's status strip shows
// them (code/StatusStrip.tsx there, Poulpe-424): its turns, with its last
// prompt's tokens on hover, and its cache counting down from its last reply.
import { useEffect, useState } from 'react'
import type { Session, SessionUsage } from '../api/types'
import { exactTime } from '../components/Time'
import type { Item } from '../transcript/types'
import { cx } from '../transcript/bits'
import { tokenText } from './Context'
import { countText } from './format'

/**
 * How long each agent's prompt cache lives, in seconds: an estimate, since no
 * ACP adapter reports it. Claude's adapter adds up the cache tokens written
 * but drops whether they were written for 5 minutes or an hour; Claude Code
 * wrote the hour in 24,028 of 24,040 replies on Fabien's Mac (25 Oct 2026).
 * Codex's is today's estimate (app/poulpe/code/codex_items.py there).
 */
const CACHE_SECONDS: Readonly<Record<string, number>> = { claude: 3600, codex: 1800 }

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

/** "41:07", "1:02:09". */
export function countdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const two = (n: number) => String(n).padStart(2, '0')
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor(total / 60) % 60
  return hours > 0 ? `${hours}:${two(minutes)}:${two(total % 60)}` : `${minutes}:${two(total % 60)}`
}

/** "under a minute", "12 min", "2 h", "3 d". */
function spanText(ms: number): string {
  if (ms < MINUTE) return 'under a minute'
  if (ms < HOUR) return `${Math.floor(ms / MINUTE)} min`
  if (ms < DAY) return `${Math.floor(ms / HOUR)} h`
  return `${Math.floor(ms / DAY)} d`
}

/**
 * When the agent last replied: when the last thing it said, thought or ran
 * began. Its requests to the model are what keep the cache, and each begins
 * one of these; the chunks that stream in after, or a tool's output, are not
 * requests, so a long tool run does age the cache.
 */
export function lastReply(items: readonly Item[]): string | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i]!
    if (item.kind === 'assistant' || item.kind === 'thinking' || item.kind === 'tool') return item.at
  }
  return null
}

/**
 * When its prompt cache is estimated to expire, in ms. Null while it works or
 * once it has ended, when its last prompt read and wrote none of the cache,
 * when its agent's cache lifetime is not known, or with no reply to count from.
 */
export function cacheExpiry(session: Pick<Session, 'state' | 'usage'>, agent: string | null, replied: string | null): number | null {
  if (session.state !== 'idle' || !agent || !replied) return null
  const seconds = CACHE_SECONDS[agent]
  const last = session.usage?.last_prompt
  if (!seconds || !last || (last.cache_read_tokens ?? 0) + (last.cache_write_tokens ?? 0) === 0) return null
  const at = Date.parse(replied)
  return Number.isNaN(at) ? null : at + seconds * 1000
}

/** The time, by the second until `until`, then by the minute. */
function useNowUntil(until: number): number {
  const [now, setNow] = useState(() => Date.now())
  const live = now < until
  useEffect(() => {
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), live ? 1000 : MINUTE)
    return () => clearInterval(timer)
  }, [until, live])
  return now
}

/** "Cache 41:07 (estimated)", then "Cache expired 12 min ago (estimated)". */
export function Cache({ expires }: { expires: number }) {
  const now = useNowUntil(expires)
  const left = expires - now
  const live = left > 0
  const title = `${live ? 'Its prompt cache expires' : 'Its prompt cache expired'} ${exactTime(new Date(expires).toISOString())} (an estimate)`
  return (
    <span className={cx('code-metric session-cache', !live && 'is-expired')} title={title}>
      <span className="session-cache-count" role={live ? 'timer' : undefined}>
        {live ? `Cache ${countdown(left)}` : `Cache expired ${spanText(-left)} ago`}
      </span>
      <span className="session-cache-estimate"> (estimated)</span>
    </span>
  )
}

/** "12 turns": the prompts sent to it. Its last prompt's tokens, as reported, on hover. */
export function Turns({ usage }: { usage: SessionUsage | undefined }) {
  const requests = usage?.prompt_requests
  if (!requests) return null
  const last = usage.last_prompt
  const tokens = last
    ? [
        `input ${tokenText(last.input_tokens)}`,
        `output ${tokenText(last.output_tokens)}`,
        ...(last.cache_read_tokens === undefined ? [] : [`cache read ${tokenText(last.cache_read_tokens)}`]),
        ...(last.cache_write_tokens === undefined ? [] : [`cache written ${tokenText(last.cache_write_tokens)}`]),
      ]
    : []
  return (
    <span className="code-metric code-usage" title={tokens.length ? `Last prompt: ${tokens.join(' · ')}` : undefined}>
      {countText(requests.started, 'turn')}
    </span>
  )
}

/** A price as its agent reported it: "$0.42", or "<$0.01" for less but some. */
export function priceText(amount: number, currency: string): string {
  let format: Intl.NumberFormat
  try {
    format = new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
  } catch {
    return `${amount.toFixed(2)} ${currency}`
  }
  return amount > 0 && amount < 0.005 ? `<${format.format(0.01)}` : format.format(amount)
}

/**
 * "84.2k tokens", and for a session billed to an API key its price too
 * (Poulpe-585): what its agent reported since it last opened, cache reads
 * left out. A plan's session shows no price: the plan pays for it.
 */
export function Spent({ usage, api }: { usage: SessionUsage | undefined; api: boolean }) {
  const tokens = usage?.new_tokens
  const cost = api ? usage?.cost : undefined
  if (tokens === undefined && !cost) return null
  const parts = [
    tokens !== undefined && <span key="tokens" title="New tokens since its agent last opened: input, output and cache writes; cache reads are left out">{tokenText(tokens)} tokens</span>,
    cost && <span key="price" className="code-usage-price" title="What its API key was charged since its agent last opened, as the agent reported it">{priceText(cost.amount, cost.currency)}</span>,
  ].filter(Boolean)
  return <span className="code-metric code-spent">{parts.flatMap((part, i) => (i ? [' · ', part] : [part]))}</span>
}
